import { Input } from '@aecom-ai-engineering/react-components';
import { Search as SearchIcon } from 'lucide-react';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AddContextCheckbox } from './add-context-checkbox';
import { AddContextListRegion } from './add-context-list-region';
import { type UseAddContextListResult } from './use-add-context-list';

/** The same pause the Findings page waits before a server-side search. */
export const SEARCH_DEBOUNCE_MS = 500;

interface AddContextPanelProps<T> {
  emptyMessage: string;
  /** Drawn before the search box, for a kind with controls of its own such as filters. */
  headerEnd?: ReactNode;
  /** The rows, and their loading state, from the kind's own paging hook. */
  list: UseAddContextListResult<T>;
  onSelectedChange: (items: T[]) => void;
  /**
   * The row for one item. The panel owns which items are selected and hands
   * each row its own state and toggle, so a card need not know about the rest
   * of the list.
   */
  renderItem: (item: T, isSelected: boolean, onToggle: (isSelected: boolean) => void) => ReactNode;
  /** The search box's text as typed. The kind's panel decides how it filters the list. */
  searchText: string;
  /** Accessible name and placeholder for the search box. */
  searchLabel: string;
  /** The search box's width; 240px unless the kind's placeholder needs more. */
  searchClassName?: string;
  onSearchTextChange: (searchText: string) => void;
  selected: T[];
  /** A table header over the rows. It stays put while the rows scroll under it. */
  listHeader?: ReactNode;
}

/**
 * Select All, the search box, the scrolling list and the selection arithmetic,
 * for either kind.
 *
 * Documents and findings differ in what they fetch and how a row is drawn, and
 * in nothing else: both track selection by id, both let Select All cover the
 * loaded rows, both page in on scroll. Those two differences arrive as `list`
 * and `renderItem`; the rest lived twice before this.
 *
 * Because selection is by id, a row ticked under one search stays ticked when
 * the next search hides it, and Select All covers only the rows that match.
 *
 * Generic over the item rather than over a kind union, so a panel keeps its own
 * concrete type end to end - `onSelectedChange` hands back `Blob[]` or
 * `Finding[]`, not something the caller has to narrow.
 */
export const AddContextPanel = <T extends { id: string | number }>({
  emptyMessage,
  headerEnd,
  listHeader,
  list: { error, isFetchingMore, isLoading, items, onScroll, triggerRef },
  onSelectedChange,
  renderItem,
  searchText,
  searchLabel,
  searchClassName = 'w-60',
  onSearchTextChange,
  selected,
}: AddContextPanelProps<T>) => {
  const { t } = useTranslation();

  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected]);

  // Select All covers what has been loaded, which is all it can: the rows below
  // the scroll have not been fetched yet.
  const allLoadedSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        const loadedIds = new Set(items.map((item) => item.id));
        onSelectedChange(selected.filter((item) => !loadedIds.has(item.id)));
        return;
      }

      const merged = [...selected];
      for (const item of items) {
        if (!selectedIds.has(item.id)) merged.push(item);
      }
      onSelectedChange(merged);
    },
    [items, onSelectedChange, selected, selectedIds],
  );

  const handleToggle = useCallback(
    (item: T, isSelected: boolean) => {
      onSelectedChange(
        isSelected ? [...selected, item] : selected.filter((entry) => entry.id !== item.id),
      );
    },
    [onSelectedChange, selected],
  );

  return (
    <>
      <div className="flex shrink-0 items-center justify-between gap-[var(--ds-space-4)]">
        <AddContextCheckbox
          checked={allLoadedSelected}
          label={t('findings.select-all')}
          onCheckedChange={handleSelectAll}
        />
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-[var(--ds-space-4)]">
          {headerEnd}
          {/* type="search" keeps the browser's native clear affordance. */}
          <Input
            className={searchClassName}
            icon={SearchIcon}
            label={searchLabel}
            onChange={(event) => onSearchTextChange(event.target.value)}
            placeholder={searchLabel}
            size="md"
            type="search"
            value={searchText}
          />
        </div>
      </div>
      <AddContextListRegion
        emptyMessage={emptyMessage}
        hasError={!!error}
        isEmpty={items.length === 0}
        isFetchingMore={isFetchingMore}
        isLoading={isLoading}
        header={listHeader}
        onScroll={onScroll}
        triggerRef={triggerRef}
      >
        {items.map((item) =>
          renderItem(item, selectedIds.has(item.id), (isSelected) =>
            handleToggle(item, isSelected),
          ),
        )}
      </AddContextListRegion>
    </>
  );
};
